import type { ReactNode } from "react"
import Container from "@/components/Container/Container"
import styles from "@/styles/components/StatusScreen.module.css"

interface StatusScreenProps {
  eyebrow?: string
  title: string
  detail?: string
  /** The underlying error text, when there is one worth showing verbatim. */
  raw?: string | null
  waiting?: boolean
  /**
   * Already inside a page — `AppShell`'s column, usually.
   *
   * Drops the full-height `Container`, which brings its own page background and
   * its own `GridBackdrop`: nested, that is a second grid over the first and a
   * 100svh block sitting under a nav that is already there.
   */
  nested?: boolean
  children?: ReactNode
}

/**
 * A whole screen that says one thing. Every state other than "running" renders
 * through here — connecting, no such session, dead listener — so the room never
 * gets a blank page or a silent spinner.
 */
export default function StatusScreen({
  eyebrow,
  title,
  detail,
  raw,
  waiting = false,
  nested = false,
  children,
}: StatusScreenProps) {
  const block = (
    <div className={nested ? `${styles.block} ${styles.nested}` : styles.block}>
      {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
      <h1 className={styles.title}>{title}</h1>
      {detail ? <p className={styles.detail}>{detail}</p> : null}
      {waiting ? (
        <div className={styles.pulse} role="status" aria-label="Connecting">
          <span />
          <span />
          <span />
        </div>
      ) : null}
      {raw ? <pre className={styles.raw}>{raw}</pre> : null}
      {children ? <div className={styles.actions}>{children}</div> : null}
    </div>
  )

  if (nested) return block
  return <Container centred>{block}</Container>
}
